import { useCallback, useEffect, useRef, useState } from 'react';
import { max, startOfDay } from 'date-fns';

import { DATE_URL_PARAM } from '~constants';
import { Product, ProductCategory } from '~types/appsync.types';
import {
  formatDateISO,
  isSelectable,
  parseCalendarDate,
  parseDateFromUrl,
  updateUrlParam,
} from '~utils';

export interface UseDateSelectorOptions {
  productCategory?: ProductCategory;
  selectedProduct?: Product;
  syncUrl?: boolean;
  /** Pre-fill date from HTML attribute (YYYY-MM-DD). Takes priority over the URL param when present. */
  initialDate?: string;
}

export interface UseDateSelectorResult {
  date: Date | undefined;
  handleDateChange: (newDate: Date | undefined) => void;
}

const formatDateParam = (d: Date): string => formatDateISO(d);

const useDateSelector = ({
  productCategory,
  selectedProduct,
  syncUrl = true,
  initialDate,
}: UseDateSelectorOptions): UseDateSelectorResult => {
  const [date, setDate] = useState<Date>();
  const prevCategoryIdRef = useRef<number>();

  // Init date from URL or initial attribute — only when the logical category changes
  useEffect(() => {
    if (!productCategory) return;
    if (prevCategoryIdRef.current === productCategory.id) return;
    prevCategoryIdRef.current = productCategory.id;

    const searchParams = new URL(globalThis.location.href).searchParams;
    const urlDate = syncUrl ? parseDateFromUrl(searchParams, new Date()) : undefined;
    const initialDateParsed =
      initialDate && !Number.isNaN(new Date(initialDate).getTime())
        ? parseCalendarDate(initialDate)
        : undefined;
    // HTML attribute takes priority over URL param
    const resolvedDate = initialDateParsed ?? urlDate;

    const minDate = max([startOfDay(productCategory.validFrom), startOfDay(new Date())]);
    const maxDate = productCategory.validTo;

    if (resolvedDate && isSelectable(resolvedDate, minDate, maxDate)) {
      setDate(resolvedDate);
    } else if (syncUrl && urlDate) {
      updateUrlParam(DATE_URL_PARAM, undefined);
    }
  }, [productCategory, syncUrl, initialDate]);

  // Invalidate date when validity category changes
  useEffect(() => {
    if (!selectedProduct || !date) return;
    const minDate = max([
      startOfDay(
        selectedProduct.firstSellableDate
          ? parseCalendarDate(selectedProduct.firstSellableDate)
          : new Date(),
      ),
      startOfDay(new Date()),
    ]);

    const maxDate = selectedProduct.lastSellableDate
      ? startOfDay(parseCalendarDate(selectedProduct.lastSellableDate))
      : undefined;

    if (startOfDay(date) < minDate || (maxDate && startOfDay(date) > maxDate)) {
      setDate(undefined);
      if (syncUrl) {
        updateUrlParam(DATE_URL_PARAM, undefined);
      }
    }
  }, [selectedProduct, date, syncUrl]);

  // Sync date to URL — done synchronously in handleDateChange to avoid
  // race conditions with the "Init from URL" effect reading stale values.
  const handleDateChange = useCallback(
    (newDate: Date | undefined) => {
      setDate(newDate);
      if (syncUrl) {
        updateUrlParam(DATE_URL_PARAM, newDate ? formatDateParam(newDate) : undefined);
      }
    },
    [syncUrl],
  );

  return {
    date,
    handleDateChange,
  };
};

export default useDateSelector;
